import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import { AuthContext } from './AuthContext';

const STORAGE_KEY = 'kzcomp-admin-tournament';

const TournamentContext = createContext({
  tournaments: [],
  selectedId: null,
  selected: null,
  setSelectedId: () => {},
  loading: true,
  refresh: async () => {},
});

const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);

export const deriveStatus = (t) => {
  const now = new Date();
  const start = toDate(t.startDate);
  const end = toDate(t.endDate);
  if (!start || !end) return t.status || 'upcoming';
  if (now < start) return 'upcoming';
  if (now > end) return 'completed';
  return 'active';
};

export const TournamentProvider = ({ children }) => {
  const { isSuperAdmin, tournamentAdminList } = useContext(AuthContext);
  const [allTournaments, setAllTournaments] = useState([]);
  const [selectedId, setSelectedIdState] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY) || null;
  });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const q = query(collection(db, 'tournaments'), orderBy('startDate', 'desc'));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => {
        const data = d.data();
        return { id: d.id, ...data, computedStatus: deriveStatus(data) };
      });
      setAllTournaments(rows);
      return rows;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setSelectedId = (id) => {
    setSelectedIdState(id);
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    }
  };

  // Tournament admins only see tournaments they're assigned to; super admins see everything.
  const tournaments = useMemo(() => {
    const isSuper = typeof isSuperAdmin === 'function' ? isSuperAdmin() : false;
    if (isSuper) return allTournaments;
    const allowed = new Set(tournamentAdminList || []);
    if (allowed.size === 0) return [];
    return allTournaments.filter((t) => allowed.has(t.id));
  }, [allTournaments, isSuperAdmin, tournamentAdminList]);

  // If the selected tournament is no longer visible to this user, clear it.
  useEffect(() => {
    if (!selectedId) return;
    const visible = tournaments.some((t) => t.id === selectedId);
    if (!visible) setSelectedId(null);
  }, [selectedId, tournaments]);

  const selected = useMemo(
    () => tournaments.find((t) => t.id === selectedId) || null,
    [tournaments, selectedId]
  );

  const value = useMemo(
    () => ({
      tournaments,
      selectedId,
      selected,
      setSelectedId,
      loading,
      refresh: load,
    }),
    [tournaments, selectedId, selected, loading]
  );

  return <TournamentContext.Provider value={value}>{children}</TournamentContext.Provider>;
};

export const useTournament = () => useContext(TournamentContext);
